import * as Events from './Events.js';
import * as SRGSettings from './SRGSettings.js';
import { PLAYER_BAR_CLASS } from './SRGLetterboxLayout.js';
import PlayerUtils from './PlayerUtils.js';
import TextTracksUtils from './TextTracksUtils.js';

const DEFAULT_PLAYBACK_RATE = 1;
const PLAYER_SMALLEST_SIZES = ['tiny', 'xsmall'];
class SRGPlaybackSettings {
  constructor(player, ConfigurationHandler) {
    this.player = player;
    this.ConfigurationHandler = ConfigurationHandler;

    this.player.textTracks()
      .on(Events.ADD_TRACK, this.textTrackAddListener.bind(this));
    this.player.on(Events.LOADED_METADATA, this.loadedMetadataListener.bind(this));
    this.player.on(Events.CAN_PLAY, this.canPlayListener.bind(this));
    this.player.on(Events.FIRST_PLAY, this.firstPlayListener.bind(this));
    this.player.on(Events.LAYOUT_BREAKPOINT_CHANGE, this.layoutBreakpointChangeListener.bind(this));
  }

  layoutBreakpointChangeListener() {
    this.restorePlaybackRate();
  }

  textTrackAddListener({ track = {} } = {}) {
    const subtitleSetting = TextTracksUtils.sanitizeParameter(this.ConfigurationHandler
      .getSetting(this.player, SRGSettings.TEXT_TRACK_LANGUAGE));
    const {
      kind, label, language, default: isTrackAutoSelected,
    } = track;

    // eslint-disable-next-line
    track.srgKind = TextTracksUtils.computeSrgKind({ kind, label });

    const isCaption = TextTracksUtils.isCaption(track.srgKind);

    if (language === subtitleSetting.language && isCaption === subtitleSetting.captions) {
      // It seems that on SRF VoDs, the text track takes a little longer to be available, so we delay the activation.
      setTimeout(() => {
        PlayerUtils.textTrackLanguage({ language, captions: isCaption }, this.player);
      }, 0);

      return;
    }

    if (isTrackAutoSelected) {
      this.hideAutoSelectedTrack(track, subtitleSetting);
    }
  }

  hideAutoSelectedTrack(track, subtitleSetting) {
    const modeChange = () => {
      if (track.mode !== 'showing') {
        return;
      }

      PlayerUtils.textTrackLanguage({
        language: subtitleSetting.language,
        captions: subtitleSetting.captions,
      }, this.player);

      track.removeEventListener(Events.MODE_CHANGE, modeChange);
    };

    track.addEventListener(Events.MODE_CHANGE, modeChange);
  }

  loadedMetadataListener() {
    const selectedAudioTrack = this.ConfigurationHandler
      .getSetting(this.player, SRGSettings.AUDIO_TRACK_LANGUAGE);

    PlayerUtils.audioTrackLanguage(selectedAudioTrack, this.player);
  }

  canPlayListener() {
    const volume = this.ConfigurationHandler.getSetting(this.player, SRGSettings.VOLUME);
    const muted = this.ConfigurationHandler.getSetting(this.player, SRGSettings.MUTED);
    const textTrackSizeLevel = this.ConfigurationHandler
      .getSetting(this.player, SRGSettings.TEXT_TRACK_SIZE_LEVEL);

    // Allows to show the right value before playing in player bar mode
    this.restorePlaybackRate();

    this.player.muted(muted);
    this.player.volume(volume);
    PlayerUtils.textTrackSizeLevel(textTrackSizeLevel, this.player);
  }

  firstPlayListener() {
    this.restorePlaybackRate();
  }

  restorePlaybackRate() {
    if (!PlayerUtils.isStreamOnDemand(this.player)) {
      return;
    }
    const size = this.player.options().currentBreakpoint;
    const isXSmallOrTinySize = PLAYER_SMALLEST_SIZES.includes(size);
    const isPlayerBar = this.player.hasClass(PLAYER_BAR_CLASS);

    const isReducedPlayerBar = isXSmallOrTinySize && isPlayerBar;

    const playbackRate = isReducedPlayerBar ? DEFAULT_PLAYBACK_RATE : this.ConfigurationHandler
      .getSetting(this.player, SRGSettings.PLAYBACK_RATE);

    this.player.playbackRate(playbackRate);
  }
}

export default SRGPlaybackSettings;
